import { useState, type ReactNode } from 'react'
import { isTransportError, useOpenWindow, useWindow } from '@krp/client-runtime'
import {
  SALES_POINTS_WINDOWS, SalesPointEditorScreen, SalesPointStatisticsScreen, SalesPointsScreen, receiptsSalesPointAudit, receiptsSalesPointRegisters,
  receiptsSalesPointReceipts, receiptsSalesPointSummary, receiptsSalesPoints, receiptsSaveSalesPoint, registerChoices, salesPointsPatched, toggleRegister, type SalesPointProblem, type SalesPointsIntent,
} from '@krp/resource-receipts-ui'
import { StatusMessage, firstOfMonth, lastOfMonth } from '@krp/ui-kit'
import { SUPER_ROLE, newUuid } from '@krp/platform-ui'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { SOURCE_KINDS } from '../../composition/connectionForms'
import { useMyAccess } from '../platform/useMyAccess'
import { todayIn, useOriginal } from './reportHooks'
import { SalesStatisticsTabs } from './SalesStatisticsTabs'

/** Отказ службы для показа: текст дословно (ADR-8) и код. */
const problemOf = (error: unknown): SalesPointProblem | null => error === undefined ? null
  : isTransportError(error) ? { message: error.message, code: error.code }
    : { message: error instanceof Error ? error.message : 'Не удалось получить ответ сервера', code: null }

/** Магазины открыты тем, у кого есть право смотреть аналитику кассиров; сервер проверит его сам. */
function SalesPointsGate({ children }: { children: ReactNode }) {
  const access = useMyAccess()
  return access.data !== undefined && !access.data.api.includes('platform:cashiers:read')
    ? <StatusMessage kind="forbidden" title="Нет права смотреть аналитику кассиров" text="Магазины доступны тем, кому выдано право просмотра аналитики кассиров." />
    : <>{children}</>
}

/** Администратор приложения (`system_admin`): только он создаёт и правит состав магазинов; сервер проверит право сам. */
/** `undefined` — права ещё читаются. */
const useIsSystemAdmin = () => useMyAccess().data?.roles.includes(SUPER_ROLE)

/** «Магазины» раздела «Статистика по продажам»: список магазинов плитками. */
export function SalesPointsBinding() {
  const directory = receiptsSalesPoints.useQuery()
  const open = useOpenWindow()
  const canCreate = useIsSystemAdmin()
  return <><SalesStatisticsTabs selected="shops" /><SalesPointsGate><SalesPointsScreen directory={directory.data} loading={directory.isFetching} error={problemOf(directory.error)} canCreate={canCreate === true}
    create={() => open(SALES_POINTS_WINDOWS.edit, { pointId: 'new' })} open={(id) => open(SALES_POINTS_WINDOWS.statistics, { pointId: id })} reload={() => void directory.refetch()} /></SalesPointsGate></>
}

/** Статистика магазина. Порядок чтения: сначала выбранный день, потом весь месяц для графика и прогноза. */
export function SalesPointStatisticsBinding() {
  const window = useWindow(), dispatch = useAppDispatch()
  const pointId = window.params.pointId ?? ''
  const intent = useAppSelector((state) => state.receipts.salesPoints)
  const today = todayIn(intent.timeZoneId)
  const from = intent.from || today, to = intent.to || today
  const directory = receiptsSalesPoints.useQuery()
  const point = directory.data?.points.find((item) => item.id === pointId)
  const points = (directory.data?.points ?? []).filter((item) => !item.archived)
  const original = useOriginal()
  const registers = registerChoices(point?.registers ?? [])
  // Выбор касс относится к открытому магазину: другой магазин начинает с «все кассы».
  const [chosenFor, setChosenFor] = useState<{ pointId: string; ids: string[] }>({ pointId: '', ids: [] })
  const chosen = chosenFor.pointId === pointId ? chosenFor.ids : []
  const month = firstOfMonth(from)
  const singleDay = from === to
  const base = { id: pointId, month, timeZoneId: intent.timeZoneId, refresh: intent.refresh }
  const monthState = receiptsSalesPointSummary.useQueryState(base, { skip: pointId === '' })
  const day = receiptsSalesPointSummary.useQuery({ ...base, from, to }, { skip: pointId === '' || !singleDay || monthState.isSuccess })
  const dayDone = !singleDay || day.isSuccess || day.isError || monthState.isSuccess
  const summary = receiptsSalesPointSummary.useQuery(base, { skip: pointId === '' || !dayDone })
  // Чеки — только для одного дня и только после итогов дня; «Показать ещё» дописывает страницы, смена дня и обновление начинают с нуля.
  const receiptsKey = `${pointId}|${from}|${to}|${chosen.join(',')}|${intent.timeZoneId}|${intent.refresh}`
  const [paging, setPaging] = useState({ key: '', offset: 0 })
  const offset = paging.key === receiptsKey ? paging.offset : 0
  const receipts = receiptsSalesPointReceipts.useQuery({ ...base, from, to, registers: chosen, offset }, { skip: pointId === '' || !singleDay || !(day.isSuccess || day.isError || monthState.isSuccess) })
  const data = summary.currentData ?? day.currentData
  const monthPending = pointId !== '' && !summary.isSuccess && !summary.isError
  const patch = (value: Partial<SalesPointsIntent>) => {
    if (value.from !== undefined || value.to !== undefined) {
      const start = value.from ?? from, end = value.to ?? to
      value = { ...value, from: start, to: end > lastOfMonth(start) ? lastOfMonth(start) : end < start ? start : end }
    }
    dispatch(salesPointsPatched(value))
  }
  return <SalesPointsGate><SalesPointStatisticsScreen point={point} pointId={pointId} points={points} selectPoint={(id) => window.replaceWith(SALES_POINTS_WINDOWS.statistics, { pointId: id })}
    registers={registers} chosenRegisters={chosen} toggleRegister={(id) => setChosenFor({ pointId, ids: toggleRegister(registers, chosen, id) })} range={{ from, to }} today={today} timeZoneId={intent.timeZoneId} summary={data}
    busy={day.isFetching || (data === undefined && summary.isFetching)} trendBusy={summary.isFetching || monthPending}
    error={problemOf(summary.error ?? (data === undefined ? day.error : undefined) ?? directory.error)}
    receipts={singleDay ? receipts.currentData : undefined} receiptsBusy={singleDay && receipts.isFetching} receiptsError={singleDay ? problemOf(receipts.error) : null}
    moreReceipts={() => setPaging({ key: receiptsKey, offset: receipts.currentData?.nextOffset ?? 0 })}
    openReceipt={original.original} openReceiptError={original.error}
    canConfigure={directory.data?.canManage === true && point?.canEdit === true} patch={patch}
    reload={() => patch({ refresh: intent.refresh + 1 })} configure={() => window.replaceWith(SALES_POINTS_WINDOWS.edit, { pointId })} /></SalesPointsGate>
}

/** Настройка магазина (`new` вместо номера — новая). После сохранения возвращает к списку. */
export function SalesPointEditBinding() {
  const window = useWindow()
  const [freshId] = useState(newUuid)
  const param = window.params.pointId ?? 'new'
  const directory = receiptsSalesPoints.useQuery()
  const point = param === 'new' ? null : directory.data?.points.find((item) => item.id === param) ?? null
  const pointId = param === 'new' ? freshId : param
  const stored = point !== null && point.version > 0
  const audit = receiptsSalesPointAudit.useQuery(pointId, { skip: !stored })
  const [save, result] = receiptsSaveSalesPoint.useMutation()
  const back = () => window.replaceWith(SALES_POINTS_WINDOWS.list)
  const useRegisters = (resourceId: string) => {
    const query = receiptsSalesPointRegisters.useQuery(resourceId, { skip: resourceId === '' })
    return { registers: query.currentData, loading: query.isFetching, error: problemOf(query.error) }
  }
  const waiting = param !== 'new' && point === null
  const systemAdmin = useIsSystemAdmin()
  return <SalesPointsGate>
    {param === 'new' && systemAdmin === false ? <StatusMessage kind="forbidden" title="Создавать магазины может администратор" text="Новый магазин создаёт администратор приложения." />
      : directory.data?.canManage === false ? <StatusMessage kind="forbidden" title="Нет права менять магазины" text="Создавать и менять магазины может тот, кому выдано право управлять кассирами." />
      : waiting ? (directory.isFetching || directory.data === undefined ? <p role="status">Загружаем магазин…</p> : <StatusMessage kind="error" title="Магазин не найден" text="Магазина с таким номером нет или он недоступен." />)
        : <SalesPointEditorScreen key={`${pointId}:${point?.version ?? 0}`} point={point} pointId={pointId} sources={directory.data?.sources ?? []} kinds={SOURCE_KINDS}
          sourcesLoading={directory.data === undefined} audit={audit.currentData} busy={result.isLoading} error={problemOf(result.error ?? audit.error)} useRegisters={useRegisters}
          save={(draft, archived) => { void save({ id: pointId, version: point?.version ?? 0, name: draft.name, archived, registers: draft.registers, operationId: newUuid() }).unwrap().then(back).catch(() => undefined) }}
          back={back} />}
  </SalesPointsGate>
}
